import { useContext } from 'react';
import { UserSessionContext } from '@/providers/UserSessionContext';
import { formatDateToFriendlyString } from '@/util/date-formatters';
import LabelValue from 'components/LabelValue';
import TransactionChart from 'components/TransactionChart';
import { Cell, Pie, PieChart, ResponsiveContainer } from 'recharts';
import { getSoCChartData } from 'util/get-soc-chart-data';

const TransactionInfo = ({ transaction, transactionConnector }) => {
  const { timezone } = useContext(UserSessionContext);
  const soh = transaction.soh ? parseInt(transaction.soh) : null;

  const { chartData, startColor, stopColor } = getSoCChartData(
    transaction.soc_start,
    transaction.soc_stop
  );

  const userStart = transaction?.user_start
    ? transaction?.user_start?.email
    : transaction?.anonymous_transaction_request?.email;

  const userStop = transaction?.timestamp_stop
    ? transaction?.user_stop
      ? transaction?.user_stop?.email
      : transaction?.anonymous_transaction_request?.email
    : '- - -';

  const registeredUser = transaction?.anonymous_transaction_request ? 'No' : 'Sí';

  return (
    <div className="transaction-info-container">
      {transactionConnector && transaction?.id ? (
        <LabelValue label="ID de transacción:" value={transaction?.id} />
      ) : null}
      {transactionConnector && transactionConnector.connector_id ? (
        <LabelValue label="N° de conector:" value={transactionConnector.connector_id} />
      ) : null}
      {transactionConnector && transactionConnector.verbose_name ? (
        <LabelValue label="Alias del conector:" value={transactionConnector.verbose_name} />
      ) : null}
      <LabelValue
        label="Inicio:"
        value={
          transaction.timestamp_start
            ? formatDateToFriendlyString(transaction.timestamp_start, timezone)
            : '- - -'
        }
      />
      <LabelValue label="Iniciada por:" value={userStart || '---'} />
      <LabelValue label="Usuario registrado:" value={registeredUser} />
      <LabelValue
        label="Término:"
        value={
          transaction.timestamp_stop
            ? formatDateToFriendlyString(transaction.timestamp_stop, timezone)
            : '- - -'
        }
      />
      <LabelValue label="Detenida por:" value={userStop || '- - -'} />
      <LabelValue
        label="Energía cargada:"
        alert={
          parseFloat(transaction.total_energy) < 0
            ? 'Es posible que este dato no sea correcto.'
            : undefined
        }
        value={
          transaction?.total_energy
            ? `${(transaction.total_energy / 1000).toFixed(2)} kWh`
            : '- - -'
        }
      />
      {transaction.vehicle ? (
        <LabelValue
          label="Vehículo:"
          value={
            transaction.vehicle.verbose_name !== transaction.vehicle.number_plate
              ? `${transaction.vehicle.verbose_name} (${transaction.vehicle.number_plate})`
              : transaction.vehicle.verbose_name
          }
        />
      ) : null}
      {transaction.soc_start && transaction.soc_stop ? (
        <>
          <h6 className="subtitle mb-2">Estado de carga (SoC):</h6>
          <div className="d-flex align-items-center justify-content-center mb-2">
            <div style={{ height: '48px', width: '48px' }}>
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={chartData}
                    dataKey="value"
                    innerRadius={8}
                    outerRadius={15}
                    startAngle={90}
                    endAngle={-270}
                    stroke="none"
                  >
                    {chartData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                </PieChart>
              </ResponsiveContainer>
            </div>
            <div className="d-flex align-items-center justify-content-center">
              <div className="d-flex flex-column">
                <div className="d-flex align-items-center">
                  <div
                    className="mr-1 rounded-circle"
                    style={{
                      backgroundColor: startColor,
                      width: '10px',
                      height: '10px',
                    }}
                  ></div>
                  <span className="font-size-12 mr-2">SoC inicial</span>
                  <b>{`${Math.round(transaction.soc_start)}%`}</b>
                </div>
                <div className="d-flex align-items-center">
                  <div
                    className="mr-1 rounded-circle"
                    style={{
                      backgroundColor: stopColor,
                      width: '10px',
                      height: '10px',
                    }}
                  ></div>
                  <span className="font-size-12 mr-2">SoC final</span>
                  <b>{`${Math.round(transaction.soc_stop)}%`}</b>
                </div>
              </div>
            </div>
          </div>
        </>
      ) : null}
      {soh ? (
        <LabelValue label="Salud de la batería (SoH):" value={`${Math.round(soh * 100)}%`} />
      ) : null}
      <TransactionChart transaction={transaction} />
    </div>
  );
};

export default TransactionInfo;
